export const discover_api_base = "/api/discover"

export const discover_status_url = `${discover_api_base}/status`
export const discover_bulk_fetch_url = `${discover_api_base}/bulk-fetch`
export const discover_list_url = (bucket) => `${discover_api_base}/list/${bucket}`
export const discover_search_url = `${discover_api_base}/search`
export const discover_single_url = `${discover_api_base}/single`

export const discover_tab_bucket_ids = [
  "kalshi_markets",
  "kalshi_events",
  "polymarket_markets",
  "polymarket_events",
]

export const build_initial_tab_filters = () => {
  const initial = {}
  discover_tab_bucket_ids.forEach((bucket_id) => {
    initial[bucket_id] = {
      search: "",
      tag: "all",
    }
  })
  return initial
}

export const discover_provider_options = [
  "kalshi",
  "polymarket",
]

export const discover_entity_options = [
  "market",
  "event",
]

export const get_lookup_options = (provider, entity) => {
  if (provider === "kalshi") {
    return ["ticker"]
  }

  if (provider === "polymarket") {
    return ["id", "slug"]
  }

  return ["ticker"]
}

export const get_default_action_config = () => {
  return {
    provider: "kalshi",
    entity: "market",
    lookup: "ticker",
    identifier: "",
  }
}

export const get_tab_action_config = (active_tab) => {
  const is_kalshi = active_tab.startsWith("kalshi")
  const is_markets = active_tab.includes("markets")

  return {
    provider: is_kalshi ? "kalshi" : "polymarket",
    entity: is_markets ? "market" : "event",
  }
}

const push_text = (arr, value) => {
  if (value === undefined || value === null) {
    return
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    arr.push(String(value))
  }
}

const collect_text_values = (raw_item, texts, depth = 0) => {
  if (!raw_item || depth > 3) {
    return
  }

  if (Array.isArray(raw_item)) {
    raw_item.forEach((child) => {
      collect_text_values(child, texts, depth + 1)
    })
    return
  }

  if (typeof raw_item === "object") {
    Object.keys(raw_item).forEach((key) => {
      push_text(texts, key)
      collect_text_values(raw_item[key], texts, depth + 1)
    })
    return
  }

  push_text(texts, raw_item)
}

const get_status_from_item = (raw_item) => {
  if (!raw_item) {
    return "unknown"
  }

  if (raw_item.status) {
    return String(raw_item.status)
  }

  if (raw_item.active === true && raw_item.closed === false) {
    return "open"
  }

  if (raw_item.closed === true) {
    return "closed"
  }

  if (raw_item.archived === true) {
    return "archived"
  }

  return "unknown"
}

const get_tag_list = (raw_item) => {
  const tag_list = []

  if (!raw_item) {
    return tag_list
  }

  if (Array.isArray(raw_item.tags)) {
    raw_item.tags.forEach((tag) => {
      if (typeof tag === "string") {
        tag_list.push(tag)
      }
      else if (tag && typeof tag === "object") {
        if (tag.label) {
          tag_list.push(String(tag.label))
        }
        if (tag.slug) {
          tag_list.push(String(tag.slug))
        }
        if (tag.name) {
          tag_list.push(String(tag.name))
        }
      }
    })
  }

  if (raw_item.category) {
    tag_list.push(String(raw_item.category))
  }

  if (raw_item.subcategory) {
    tag_list.push(String(raw_item.subcategory))
  }

  return Array.from(new Set(tag_list.filter(Boolean)))
}

const to_item_array = (raw_payload, action_config) => {
  const payload = raw_payload?.data ? raw_payload.data : raw_payload
  const requested_entity = action_config?.entity

  if (Array.isArray(payload)) {
    return payload
  }

  if (!payload || typeof payload !== "object") {
    return []
  }

  // Single-fetch requests should honor the selected entity before generic array fallbacks.
  if (requested_entity === "event") {
    if (payload.event && typeof payload.event === "object") {
      return [payload.event]
    }
    if (payload.series_ticker || payload.slug || payload.title || payload.sub_title) {
      return [payload]
    }
  }

  if (requested_entity === "market") {
    if (payload.market && typeof payload.market === "object") {
      return [payload.market]
    }
    if (Array.isArray(payload.markets)) {
      return payload.markets
    }
    if (payload.ticker || payload.conditionId || payload.marketType || payload.market_type) {
      return [payload]
    }
  }

  if (Array.isArray(payload.markets)) {
    return payload.markets
  }

  if (Array.isArray(payload.events)) {
    return payload.events
  }

  if (Array.isArray(payload.items)) {
    return payload.items
  }

  if (payload.market && typeof payload.market === "object") {
    return [payload.market]
  }

  if (payload.event && typeof payload.event === "object") {
    return [payload.event]
  }

  return [payload]
}

const infer_entity = (raw_item, fallback_entity) => {
  // Kalshi Market has `market_type`; Kalshi EventData has `event_ticker` but not market_type.
  if (
    raw_item?.conditionId
    || raw_item?.question
    || raw_item?.marketType
    || raw_item?.market_type
  ) {
    return "market"
  }

  if (raw_item?.markets || raw_item?.series_ticker || raw_item?.subtitle) {
    return "event"
  }

  return fallback_entity
}

const to_number_or_null = (value) => {
  if (value === undefined || value === null || value === "") {
    return null
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const first_non_empty = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue
    }
    const s = String(value).trim()
    if (s) {
      return s
    }
  }
  return ""
}

const build_display_for_item = (raw_item, normalized_entity, fallback_item_id) => {
  const primary_id = first_non_empty(
    raw_item?.id,
    raw_item?.ticker,
    raw_item?.event_ticker,
    raw_item?.conditionId,
    raw_item?.questionID,
    raw_item?.slug,
    fallback_item_id
  )

  const identity_pairs = [
    ["id", raw_item?.id],
    ["ticker", raw_item?.ticker],
    ["event_ticker", raw_item?.event_ticker],
    ["slug", raw_item?.slug || raw_item?.seriesSlug],
    ["condition_id", raw_item?.conditionId],
    ["question_id", raw_item?.questionID],
    ["series_ticker", raw_item?.series_ticker],
  ]
  const seen_identity = new Set()
  const secondary_ids = identity_pairs
    .filter((pair) => pair[1] !== undefined && pair[1] !== null && String(pair[1]).trim() !== "")
    .filter((pair) => {
      const key = `${pair[0]}:${String(pair[1])}`
      if (seen_identity.has(key)) {
        return false
      }
      seen_identity.add(key)
      return true
    })
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  const timing_pairs = [
    ["open", raw_item?.open_time || raw_item?.startDate || raw_item?.start_date],
    ["close", raw_item?.close_time || raw_item?.endDate || raw_item?.end_date],
    ["expiry", raw_item?.expiration_time || raw_item?.latest_expiration_time],
    ["updated", raw_item?.updated_time || raw_item?.last_updated_ts || raw_item?.updatedAt],
    ["created", raw_item?.created_time || raw_item?.createdAt],
  ]
    .filter((pair) => pair[1])
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  const market_count = Array.isArray(raw_item?.markets)
    ? raw_item.markets.length
    : to_number_or_null(raw_item?.market_count)

  const price_pairs = [
    ["last", raw_item?.last_price || raw_item?.last_price_dollars],
    ["yes_bid", raw_item?.bestBid || raw_item?.yes_bid || raw_item?.yes_bid_dollars],
    ["yes_ask", raw_item?.bestAsk || raw_item?.yes_ask || raw_item?.yes_ask_dollars],
    ["no_bid", raw_item?.no_bid || raw_item?.no_bid_dollars],
    ["no_ask", raw_item?.no_ask || raw_item?.no_ask_dollars],
  ]
    .filter((pair) => pair[1] !== undefined && pair[1] !== null && String(pair[1]).trim() !== "")
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  const activity_pairs = [
    ["volume_24h", raw_item?.volume24hr || raw_item?.volume_24h || raw_item?.volume_24h_fp],
    ["volume_total", raw_item?.volume || raw_item?.volumeNum || raw_item?.volume_fp],
    ["open_interest", raw_item?.open_interest || raw_item?.open_interest_fp],
    ["liquidity", raw_item?.liquidity || raw_item?.liquidity_dollars || raw_item?.liquidityNum],
  ]
    .filter((pair) => pair[1] !== undefined && pair[1] !== null && String(pair[1]).trim() !== "")
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  const meta_pairs = [
    ["category", raw_item?.category],
    ["subcategory", raw_item?.subcategory],
    ["series", raw_item?.series_ticker],
    ["market_type", raw_item?.market_type || raw_item?.marketType],
    ["collateral", raw_item?.collateral_return_type],
  ]
    .filter((pair) => pair[1] !== undefined && pair[1] !== null && String(pair[1]).trim() !== "")
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  const resolution_pairs = [
    ["result", raw_item?.result],
    ["outcome", raw_item?.outcome],
    ["resolution", raw_item?.resolution],
    ["settlement", raw_item?.settlement_value || raw_item?.settlement_value_dollars],
  ]
    .filter((pair) => pair[1] !== undefined && pair[1] !== null && String(pair[1]).trim() !== "")
    .map((pair) => ({ label: pair[0], value: String(pair[1]) }))

  return {
    primary_id: primary_id,
    secondary_ids: secondary_ids,
    timing: timing_pairs,
    market_count: market_count,
    pricing: normalized_entity === "market" ? price_pairs : [],
    activity: normalized_entity === "market" ? activity_pairs : [],
    meta: meta_pairs,
    resolution: resolution_pairs,
  }
}

export const normalize_discover_data = (raw_payload, action_config) => {
  const raw_items = to_item_array(raw_payload, action_config)

  return raw_items.map((raw_item, idx) => {
    const text_values = []
    collect_text_values(raw_item, text_values)

    const id =
      raw_item?.id
      || raw_item?.ticker
      || raw_item?.event_ticker
      || raw_item?.conditionId
      || raw_item?.questionID
      || raw_item?.slug
      || `item-${idx}`

    const ticker = raw_item?.ticker || raw_item?.event_ticker || ""
    const slug = raw_item?.slug || raw_item?.seriesSlug || ""
    const title =
      raw_item?.title
      || raw_item?.question
      || raw_item?.sub_title
      || raw_item?.subtitle
      || raw_item?.groupItemTitle
      || raw_item?.yes_sub_title
      || raw_item?.no_sub_title
      || `Untitled ${action_config.entity}`

    const description =
      raw_item?.description
      || raw_item?.rules_primary
      || raw_item?.rules_secondary
      || raw_item?.subtitle
      || raw_item?.sub_title
      || ""

    const entity = infer_entity(raw_item, action_config.entity)
    const display = build_display_for_item(raw_item, entity, id)

    return {
      item_id: id,
      provider: action_config.provider,
      entity: entity,
      title: String(title),
      description: String(description || ""),
      ticker: String(ticker || ""),
      slug: String(slug || ""),
      status: get_status_from_item(raw_item),
      tag_list: get_tag_list(raw_item),
      search_blob: text_values.join(" ").toLowerCase(),
      display: display,
      raw_item: raw_item,
    }
  })
}

export const get_unique_tags = (items) => {
  const tag_map = {}

  items.forEach((item) => {
    item.tag_list.forEach((tag) => {
      tag_map[tag] = true
    })
  })

  return Object.keys(tag_map).sort((a, b) => a.localeCompare(b))
}
